import type { SlideEntry } from "./types";

export const SIDEBAR_WIDTH = 232;

interface Props {
  slides: SlideEntry[];
  currentIndex: number;
  onSelect: (index: number) => void;
}

export function SlideIndex({ slides, currentIndex, onSelect }: Props) {
  const appendixStartIndex = slides.findIndex(
    (s) => s.sectionBefore === "Appendices",
  );

  return (
    <aside
      className="fixed left-0 top-0 bottom-0 z-10 overflow-y-auto bg-blue-950"
      style={{ width: SIDEBAR_WIDTH }}
      aria-label="Slide index"
    >
      <div className="flex min-h-full flex-col justify-center p-3">
        <header className="mb-6 flex justify-start px-2">
          <img
            src="https://public.apptonomy.ai/a7y-wordmark-ondark.png"
            srcSet="https://public.apptonomy.ai/a7y-wordmark-ondark.png 1x, https://public.apptonomy.ai/a7y-wordmark-ondark_2x.png 2x, https://public.apptonomy.ai/a7y-wordmark-ondark_3x.png 3x"
            alt="Apptonomy"
            className="h-6 w-auto"
          />
        </header>
        <ol className="flex flex-col gap-0.5">
          {slides.map((slide, i) => {
            const isActive = i === currentIndex;
            const isAppendix =
              appendixStartIndex !== -1 && i >= appendixStartIndex;
            const numberLabel = isAppendix
              ? String.fromCharCode(65 + (i - appendixStartIndex))
              : String(i + 1).padStart(2, "0");
            return (
              <li key={slide.id}>
                {slide.sectionBefore ? (
                  <div
                    className={`px-2 pb-1 text-[9px] font-semibold uppercase tracking-[0.18em] text-slate-400 ${
                      i === 0 ? "" : "pt-2.5"
                    }`}
                  >
                    {slide.sectionBefore}
                  </div>
                ) : null}
                <button
                  type="button"
                  onClick={(e) => {
                    onSelect(i);
                    // Drop focus after a click so the clicked button doesn't
                    // retain focus and light up its focus-visible ring on the
                    // next keyboard navigation keypress (global handler in
                    // DeckPage drives slide changes, not button focus).
                    e.currentTarget.blur();
                  }}
                  aria-current={isActive ? "true" : undefined}
                  aria-label={`Go to slide ${i + 1}: ${slide.title}`}
                  className={`group block w-full rounded border-l-2 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-300 ${
                    isActive ? "border-blue-400" : "border-transparent"
                  }`}
                >
                  <div className="flex items-center gap-1.5 px-2 py-1">
                    <span
                      className={`w-4 shrink-0 text-right text-[10px] tabular-nums ${
                        isActive
                          ? "font-bold text-blue-300"
                          : "font-semibold text-slate-500 group-hover:text-slate-300"
                      }`}
                    >
                      {numberLabel}
                    </span>
                    <span
                      className={`line-clamp-2 text-[11px] leading-tight ${
                        isActive
                          ? "font-semibold text-white"
                          : "font-medium text-slate-400 group-hover:text-slate-200"
                      }`}
                    >
                      {slide.title}
                    </span>
                  </div>
                </button>
              </li>
            );
          })}
        </ol>
      </div>
    </aside>
  );
}
